/** Gateways that appear as card payment options (same list as checkout). */
export const CHECKOUT_CARD_GATEWAY_KEYS = ["stripe", "paypal", "razorpay", "paystack", "mercadopago"];

export const DISPLAY_USD = { symbol: "$", code: "USD", rate: 1 };

/**
 * Last-resort NGN display when gateway payload has no Paystack NGN rate yet (e.g. loading).
 * Keep in sync with backend `USD_TO_CURRENCY_FALLBACK.NGN` in payment.service.ts (~Frankfurter gap).
 */
export const DISPLAY_NGN_DEFAULT = { symbol: "₦", code: "NGN", rate: 1550 };

function ngnFromPaystackSettlement(gatewaysData) {
  const st = gatewaysData?.paystack?.settlement;
  if (!st || typeof st !== "object") return null;
  if (String(st.code || "").toUpperCase() !== "NGN") return null;
  const rate = Number(st.rate);
  if (!(rate > 0)) return null;
  return { symbol: st.symbol || "₦", code: "NGN", rate };
}

/** Paystack card row settlement → plan/checkout display (USD, NGN, GHS). */
function displayFromSettlement(st) {
  if (!st || typeof st !== "object") return DISPLAY_NGN_DEFAULT;
  const code = String(st.code || "").toUpperCase();
  const rate = Number(st.rate);
  if (code === "USD") return DISPLAY_USD;
  if (code === "NGN") {
    return rate > 0 ? { symbol: st.symbol || "₦", code: "NGN", rate } : DISPLAY_NGN_DEFAULT;
  }
  if (code === "GHS" && rate > 0) {
    return { symbol: st.symbol || "₵", code: "GHS", rate };
  }
  if (!(rate > 0)) return DISPLAY_USD;
  return { symbol: st.symbol || code, code, rate };
}

/**
 * Plan / checkout summary display currency.
 * - Single active Paystack + NGN → NGN, {@link isSinglePaystackNgn} true (redundant Paystack caption hidden).
 * - Paystack NGN active with other card gateways → NGN summary for everyone (default product currency).
 * - Single non-Paystack (e.g. Stripe only) → USD.
 * - No active gateways → NGN from Paystack config if available, else {@link DISPLAY_NGN_DEFAULT}.
 * - Multiple actives without Paystack NGN → USD.
 */
export function resolvePlanDisplayCurrency(gatewaysData) {
  const data = gatewaysData && typeof gatewaysData === "object" ? gatewaysData : {};
  const active = CHECKOUT_CARD_GATEWAY_KEYS.map((k) => (data[k]?.active ? [k, data[k]] : null)).filter(Boolean);
  const paystackActive = !!data.paystack?.active;
  const ngnPs = ngnFromPaystackSettlement(data);

  if (active.length === 1) {
    const [key, gw] = active[0];
    if (key === "paystack") {
      const st = gw?.settlement;
      const code = String(st?.code || "").toUpperCase();
      if (code === "NGN" && st && Number(st.rate) > 0) {
        return {
          currency: { symbol: st.symbol || "₦", code: "NGN", rate: Number(st.rate) },
          isSinglePaystackNgn: true,
        };
      }
      return { currency: displayFromSettlement(st), isSinglePaystackNgn: false };
    }
    return { currency: DISPLAY_USD, isSinglePaystackNgn: false };
  }

  if (paystackActive && ngnPs) {
    return { currency: ngnPs, isSinglePaystackNgn: false };
  }

  if (active.length === 0) {
    return { currency: ngnPs || DISPLAY_NGN_DEFAULT, isSinglePaystackNgn: false };
  }

  return { currency: DISPLAY_USD, isSinglePaystackNgn: false };
}

export function formatPlanPriceInDisplay(usdAmount, displayCurrency) {
  const usd = parseFloat(String(usdAmount ?? "0")) || 0;
  const rate = Number(displayCurrency?.rate) > 0 ? Number(displayCurrency.rate) : 1;
  const local = usd * rate;
  if (String(displayCurrency?.code || "").toUpperCase() === "NGN") {
    return Math.round(local).toLocaleString();
  }
  return local.toFixed(2);
}
